import type { ReactNode } from 'react';
import { AlertCircle, Inbox, Loader2, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from './Button';

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-5 w-5 animate-spin text-primary', className)} aria-hidden />;
}

export function LoadingState({ className, label }: { className?: string; label?: string }) {
  const { t } = useTranslation();
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 py-16 text-ink-muted', className)} role="status">
      <Spinner className="h-7 w-7" />
      <p className="text-sm">{label ?? t('common.loading')}</p>
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton h-4 w-full', className)} aria-hidden />;
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 px-6 py-14 text-center', className)}>
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-muted text-ink-subtle">
        {icon ?? <Inbox className="h-6 w-6" aria-hidden />}
      </div>
      <div className="max-w-sm space-y-1">
        <h3 className="font-display text-base font-semibold text-ink">{title}</h3>
        {description ? <p className="text-sm text-ink-muted">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

interface ErrorStateProps {
  /** Message impose par l'appelant, prioritaire sur tout le reste. */
  message?: string;
  /** L'echec tel qu'il est remonte : son code et sa traduction valent mieux qu'un texte generique. */
  error?: unknown;
  onRetry?: () => void;
  className?: string;
}

/**
 * Un refus de droits n'est pas une panne.
 *
 * Presenter « Une erreur est survenue — Impossible de charger les donnees »
 * a quelqu'un dont le role ne couvre pas la page lui fait croire au defaut
 * technique, et le bouton « Reessayer » l'invite a s'obstiner. L'API renvoie
 * deja un motif traduit : autant le dire, et retirer le bouton.
 *
 * A refusal is not an outage: say so, and drop the retry button.
 */
export function ErrorState({ message, error, onRetry, className }: ErrorStateProps) {
  const { t } = useTranslation();
  const forbidden = error instanceof ApiError && error.status === 403;
  const detail =
    message ?? (error instanceof ApiError ? error.localized() : undefined) ??
    (forbidden ? t('common.forbiddenDefault') : t('common.errorRetry'));

  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 px-6 py-14 text-center', className)} role="alert">
      <div
        className={cn(
          'flex h-12 w-12 items-center justify-center rounded-2xl',
          forbidden ? 'bg-surface-muted text-ink-subtle' : 'bg-danger-soft text-danger',
        )}
      >
        {forbidden ? <Lock className="h-6 w-6" aria-hidden /> : <AlertCircle className="h-6 w-6" aria-hidden />}
      </div>
      <div className="max-w-sm space-y-1">
        <h3 className="font-display text-base font-semibold text-ink">
          {t(forbidden ? 'common.forbiddenTitle' : 'common.errorTitle')}
        </h3>
        <p className="text-sm text-ink-muted">{detail}</p>
      </div>
      {onRetry && !forbidden ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      ) : null}
    </div>
  );
}
